/**
 * Custom Request date selection page.
 * Entry point for the custom request flow. Shows a calendar to pick a date.
 * @module Widget/CustomRequest/CustomRequestPage
 */

import dayjs from 'dayjs';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useAtom } from 'jotai';
import { isCustomRequestAllowedForDate } from './utils.ts';
import { FORM_CONSTRAINTS } from './constants.ts';
import {
  useWidgetState,
  useRestaurantId,
  AuthAtom,
  RestaurantAtom,
  RestaurantMetaAtom,
} from '../lib/state.ts';
import { getRestaurant } from '@/lib/api.ts';
import { track } from '@/lib/analytics/index.ts';
import type { RestaurantResponseData } from '@/lib/types.ts';
import styles from './CustomRequest.module.css';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Calendar-based date selection for custom request flow.
 * Validates dates against restaurant's special_request_days and per-date availability.
 * Always navigates to auth (details) page to enforce phone verification.
 *
 * @returns Rendered date selection page
 */
const CustomRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const restaurantId = useRestaurantId();
  const [storedRestaurant, setStoredRestaurant] = useAtom(RestaurantAtom);
  const auth = useAtomValue(AuthAtom);
  const [meta, setMeta] = useAtom(RestaurantMetaAtom);
  const [widget, updateWidget] = useWidgetState();

  const [localRestaurant, setLocalRestaurant] = useState<RestaurantResponseData | null>(storedRestaurant);

  /** Fetch restaurant data when the atom is empty (e.g. after page refresh). */
  useEffect(() => {
    if (storedRestaurant) {
      setLocalRestaurant(storedRestaurant);
      return;
    }
    if (!restaurantId) return;
    const date = new Date().toISOString().slice(0, 10);
    void getRestaurant(restaurantId, date, '0:00', 'request', auth?.token ?? null).then((res) => {
      if (res?.data) {
        setLocalRestaurant(res.data);
        setStoredRestaurant(res.data);
      }
      if (res?.meta) {
        setMeta(res.meta);
      }
    });
  }, [restaurantId, storedRestaurant, auth?.token, setMeta]);

  const restaurant = localRestaurant;

  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const isSameRestaurant = widget.custom_request_restaurant_id === restaurantId;
  const [selectedDate, setSelectedDate] = useState<string | null>(
    isSameRestaurant ? (widget.custom_request?.date ?? null) : null
  );

  const today = dayjs().startOf('day');
  const availDates = meta?.dates;

  const maxDate = useMemo(() => {
    if (availDates && availDates.length > 0) {
      const lastDate = availDates[availDates.length - 1];
      return dayjs(lastDate.date);
    }
    return dayjs().add(FORM_CONSTRAINTS.DEFAULT_BOOKING_WINDOW_DAYS, 'day');
  }, [availDates]);

  const calendarDays = useMemo(() => {
    const start = currentMonth.startOf('month').startOf('week');
    const end = currentMonth.endOf('month').endOf('week');
    const days: dayjs.Dayjs[] = [];
    let day = start;
    while (day.isBefore(end) || day.isSame(end, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }
    return days;
  }, [currentMonth]);

  const isDateDisabled = useCallback(
    (date: dayjs.Dayjs): boolean => {
      if (date.isBefore(today, 'day')) return true;
      if (date.isAfter(maxDate, 'day')) return true;
      if (!date.isSame(currentMonth, 'month')) return true;
      return !isCustomRequestAllowedForDate(restaurant, date.format('YYYY-MM-DD'), availDates);
    },
    [restaurant, availDates, today, maxDate, currentMonth]
  );

  const handleDateClick = useCallback(
    (date: dayjs.Dayjs) => {
      if (isDateDisabled(date)) return;
      setSelectedDate(date.format('YYYY-MM-DD'));
    },
    [isDateDisabled]
  );

  const handleContinue = useCallback(() => {
    if (!selectedDate || !restaurantId) return;

    const prev = widget.custom_request;
    const restaurantChanged = widget.custom_request_restaurant_id !== restaurantId;
    const dateChanged = prev?.date !== selectedDate;
    const needsReset = restaurantChanged || dateChanged;

    const freshRequest = {
      date: selectedDate,
      party_size: restaurantChanged
        ? (restaurant?.min_size ?? 2)
        : (prev?.party_size ?? restaurant?.min_size ?? 2),
      selected_times: [],
      selected_table_types: [],
      selected_table_type_names: [],
      per_person_spend: 0,
      minimum_spend: 0,
    };

    updateWidget({
      bookingType: 'custom_request',
      phone_confirmed: null,
      custom_request: needsReset ? freshRequest : { ...prev, date: selectedDate },
      custom_request_step: needsReset ? 0 : widget.custom_request_step ?? 0,
      custom_request_restaurant_id: restaurantId,
    });

    track('Widget Continue to Details', {
      restaurant_id: restaurantId,
      booking_type: 'custom_request',
    });

    navigate('../details');
  }, [selectedDate, restaurantId, restaurant, widget, updateWidget, navigate]);

  const canGoBack = currentMonth.isAfter(dayjs(), 'month');
  const canGoForward = currentMonth.isBefore(maxDate.startOf('month'), 'month');

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Back button */}
        <button className={styles.backButton} onClick={() => navigate('../booking')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Header */}
        <div>
          <h2 className={styles.sectionTitle}>Custom Request</h2>
          <p className={styles.sectionSubtitle}>
            Select a date for {restaurant?.name ?? 'your request'}
          </p>
        </div>

        {/* Month navigation */}
        <div className={styles.monthNav}>
          <button
            className={styles.monthArrow}
            onClick={() => setCurrentMonth((m) => m.subtract(1, 'month'))}
            disabled={!canGoBack}
            aria-label="Previous month"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <span className={styles.monthLabel}>
            {currentMonth.format('MMMM YYYY')}
          </span>
          <button
            className={styles.monthArrow}
            onClick={() => setCurrentMonth((m) => m.add(1, 'month'))}
            disabled={!canGoForward}
            aria-label="Next month"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        {/* Calendar */}
        <div className={styles.calendarGrid}>
          {DAYS_OF_WEEK.map((day) => (
            <div key={day} className={styles.calendarDayHeader}>
              {day}
            </div>
          ))}

          {calendarDays.map((date) => {
            const dateStr = date.format('YYYY-MM-DD');
            const disabled = isDateDisabled(date);
            const isSelected = selectedDate === dateStr;
            const isCurrentMonth = date.isSame(currentMonth, 'month');

            let dayClass = styles.calendarDay;
            if (!isCurrentMonth) dayClass = styles.calendarDayHidden;
            else if (isSelected) dayClass = styles.calendarDaySelected;
            else if (disabled) dayClass = styles.calendarDayDisabled;

            return (
              <button
                key={dateStr}
                className={dayClass}
                onClick={() => handleDateClick(date)}
                disabled={disabled || !isCurrentMonth}
              >
                {date.date()}
              </button>
            );
          })}
        </div>

        <p className={styles.bookingWindowInfo}>
          {restaurant?.name ?? 'This restaurant'} accepts bookings up to{' '}
          {availDates && availDates.length > 1
            ? availDates.length - 1
            : FORM_CONSTRAINTS.DEFAULT_BOOKING_WINDOW_DAYS}{' '}
          days in advance
        </p>

        {/* Continue button */}
        <button
          className={styles.ctaButton}
          onClick={handleContinue}
          disabled={!selectedDate}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default CustomRequestPage;
